import { useTranslation } from 'react-i18next';
import LockOutlined from '@mui/icons-material/LockOutlined';
import styles from './PriceDeviationTable.module.css';
import classNames from 'classnames';
import { getRenderablePriceDeviationsData } from '@/services/menu/menus.helper.service';
import { PriceDeviation, PriceDeviationResponse } from '@/types/menus.types';

interface PriceDeviationsTableProps {
  data: PriceDeviationResponse[];
  /** Optional override for the first column header (e.g. "BUNDLE ADD-ON" for named price drawer) */
  firstColumnHeader?: string;
  /** When true, display "0.00" as "None" (e.g. bundle upcharges) */
  formatZeroPriceAsNone?: boolean;
}

export const PriceDeviationsTable = ({
  data,
  firstColumnHeader,
  formatZeroPriceAsNone = false,
}: PriceDeviationsTableProps) => {
  const { t } = useTranslation();
  const majorityExists = data.find((pdr: PriceDeviationResponse) => pdr.majorityPricePoint !== null);

  const { sortedStores, renderableData } = getRenderablePriceDeviationsData(data);
  const productsHeaderLabel = firstColumnHeader ?? t('priceDeviations.table.products');

  const formatPrice = (price: string | null | undefined) => {
    if (price === 'Inactive') return price;
    if (formatZeroPriceAsNone && price === '0.00') return 'None';
    return price ? `$${price}` : formatZeroPriceAsNone ? '--' : '';
  };

  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th rowSpan={2} className={styles.productsHeader}>
            {productsHeaderLabel}
          </th>
          {majorityExists && (
            <th rowSpan={2} className={styles.majorityHeader}>
              {t('priceDeviations.table.majorityPricePoint')}
            </th>
          )}
          <th colSpan={sortedStores.length} className={styles.deviationsHeader}>
            {t('priceDeviations.table.priceDeviationsByStore')}
          </th>
        </tr>
        <tr>
          {sortedStores.map((store: string) => (
            <td className={styles.storeHeader} key={store}>
              {store}
            </td>
          ))}
        </tr>
      </thead>
      <tbody>
        {renderableData.map((product: PriceDeviationResponse, index: number) => (
          <tr
            className={classNames({ [styles.evenRow]: index % 2 === 0 })}
            key={product.productName + product.productDescription}
          >
            <td>
              <div>
                <div className={styles.productName}>
                  {product.isLock ? (
                    <span className={styles.productNameWithLock}>
                      <LockOutlined className={styles.lockIcon} aria-hidden />
                      {product.productName}
                    </span>
                  ) : (
                    product.productName
                  )}
                </div>{' '}
                <div className={styles.productDescription}>{product.productDescription}</div>
              </div>
            </td>
            {majorityExists && (
              <td
                className={classNames(styles.majorityPrice, {
                  [styles.majorityEven]: index % 2 === 0,
                  [styles.majorityOdd]: index % 2 !== 0,
                })}
              >
                {product.majorityPricePoint === 'Inactive'
                  ? product.majorityPricePoint
                  : formatPrice(product.majorityPricePoint ?? undefined) || '--'}
              </td>
            )}
            {product.priceDeviations.map((deviation: PriceDeviation, count: number) => (
              <td
                className={classNames(styles.price, { [styles.firstPrice]: count === 0 })}
                key={deviation.storeNumber}
              >
                {deviation.price === 'Inactive'
                  ? deviation.price
                  : formatZeroPriceAsNone
                  ? formatPrice(deviation.price) || '--'
                  : deviation.price
                  ? `$${deviation.price}`
                  : ''}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};
